/**
 * The welcome tour: a card that walks a new person through the real screens,
 * pointing at what each stop talks about. Its stops are data (`tours.ts`).
 *
 * It starts by itself the first time a person opens Home, once per person
 * (`GET /api/v1/tours`, kept on the server so another browser doesn't show it
 * again), and Home's "Take the tour" replays it. Finishing or skipping is
 * recorded; a session the server keeps nothing for is remembered in the browser.
 *
 * Placement, in both of the app's settings:
 * - On its own, the page scrolls as usual, so the card sits in document
 *   coordinates next to what it points at, and the target is scrolled into view.
 * - Embedded in a Rumio site, the frame is as tall as its content and the host
 *   page scrolls (see `lib/visible-area.ts`). Document coordinates are still
 *   right next to a target; a card with no target goes in the visible part.
 *
 * The card is a non-modal dialog: the screen behind it still works. Esc skips
 * (on the last stop it finishes), ← and → move. Before taking the person to
 * another screen while theirs has unsaved edits (`lib/unsaved.ts`), it asks.
 */

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { helpWidgetKey } from "@/lib/help";
import { inIframe } from "@/lib/iframe-bridge";
import type { Overview } from "@/lib/setup";
import type { Campaign, CampaignSettings } from "@/lib/types";
import { hasUnsaved } from "@/lib/unsaved";
import { remeasureVisibleArea, useVisibleArea } from "@/lib/visible-area";
import { LearnMore } from "../LearnMore";
import { Button, ConfirmDialog, cn } from "../ui";
import { TOUR_VERSION, chooseTour, stopsFor, type ShownStop, type TourContext } from "./tours";

const LOCAL_KEY = "lettrova.tour.welcome";
/** Where a tour in progress is kept for the tab, so a reload (or a host that reloads the frame) resumes it. */
const RUN_KEY = "lettrova.tour.run";
/** A kept tour older than this is dropped rather than resumed. */
const RUN_TTL_MS = 30 * 60 * 1000;
/** How long a stop waits for what it points at before showing the card in the middle. */
const TARGET_WAIT_MS = 2500;
const POLL_MS = 150;
/** Room left for a host site's sticky header over the frame (the same allowance `ConfirmDialog` makes). */
const HOST_HEADER_PX = 112;
/** Embedded, how often a stop asks again which part of the frame is in view. */
const REMEASURE_MS = 400;
/** How long after a stop opens it keeps bringing its target back into view. */
const SETTLE_MS = 3000;
/** How dark the page around what a stop points at, or behind a card on its own, goes. */
const DIM = "rgb(0 0 0 / 0.45)";

interface TourApi {
  /** Start the tour from its first stop (the "Take the tour" button). */
  start: () => void;
  active: boolean;
}

const TourContextValue = createContext<TourApi>({ start: () => {}, active: false });

export function useTour(): TourApi {
  return useContext(TourContextValue);
}

function localSeen(): boolean {
  try {
    return Number(localStorage.getItem(LOCAL_KEY) ?? 0) >= TOUR_VERSION;
  } catch {
    return false;
  }
}

function rememberLocally() {
  try {
    localStorage.setItem(LOCAL_KEY, String(TOUR_VERSION));
  } catch {
    // Private windows and blocked storage: the tour may show once more, nothing worse.
  }
}

/** Whether this person has finished or skipped the current tour. */
async function alreadySeen(): Promise<boolean> {
  try {
    const r = await api.get<{ data: { persistent: boolean; tours: Record<string, { version: number } | undefined> } }>("/api/v1/tours");
    if (!r.data.persistent) return localSeen();
    return (r.data.tours.welcome?.version ?? 0) >= TOUR_VERSION;
  } catch {
    // Unknown is treated as seen: a tour that keeps popping up is worse than one missed.
    return true;
  }
}

async function record(outcome: "finished" | "skipped") {
  try {
    const r = await api.post<{ data: { persistent: boolean } }>("/api/v1/tours/welcome", { version: TOUR_VERSION, outcome });
    if (!r.data.persistent) rememberLocally();
  } catch {
    rememberLocally();
  }
}

/** The oldest draft waiting on a store, or null. */
async function oldestWaiting(storeId: string): Promise<string | null> {
  try {
    const rows = (await api.get<{ data: Campaign[] }>(`/api/v1/stores/${storeId}/campaigns`)).data;
    const waiting = rows
      .filter((c) => c.status === "pending_approval")
      .sort((a, b) => a.scheduled_send_at.localeCompare(b.scheduled_send_at));
    return waiting[0]?.id ?? null;
  } catch {
    return null;
  }
}

/** Where a store's "draft is ready" emails go, as Brand says: the people listed, else the reply-to. */
async function notifyAddresses(storeId: string): Promise<string[]> {
  try {
    const { brand } = (await api.get<{ data: CampaignSettings }>(`/api/v1/stores/${storeId}/campaign-settings`)).data;
    if (brand.notifyEmails?.length) return brand.notifyEmails;
    return brand.replyTo ? [brand.replyTo] : [];
  } catch {
    return [];
  }
}

/** What the tour shows: the account's state, the oldest draft waiting on the store it shows, and where its emails go. */
async function resolveContext(): Promise<TourContext> {
  let overview: Overview | null = null;
  try {
    overview = (await api.get<{ data: Overview }>("/api/v1/overview")).data;
  } catch {
    // The tour still works without it: the setup version, nothing pointed at that isn't there.
  }
  const chosen = chooseTour(overview);
  const review = chosen.kind === "review" && chosen.storeId !== null;
  const [draftId, notifyEmails, helpBubble] = await Promise.all([
    review && chosen.draftsWaiting > 0 ? oldestWaiting(chosen.storeId!) : null,
    review ? notifyAddresses(chosen.storeId!) : [],
    // On its own, the app shows the help bubble when it has a key. Embedded, "Learn more" asks the host
    // to open its widget, but a host site needn't show a bubble (lettrova.com doesn't), so none is promised.
    inIframe() ? false : helpWidgetKey().then(Boolean),
  ]);
  return { ...chosen, draftId, notifyEmails, helpBubble };
}

interface Run {
  ctx: TourContext;
  stops: ShownStop[];
  index: number;
}

function keepRun(run: Run | null) {
  try {
    if (run) sessionStorage.setItem(RUN_KEY, JSON.stringify({ version: TOUR_VERSION, at: Date.now(), ctx: run.ctx, index: run.index }));
    else sessionStorage.removeItem(RUN_KEY);
  } catch {
    // Storage blocked: a reload ends the tour, nothing worse.
  }
}

/** The tour this tab was in the middle of, if any. */
function keptRun(): Run | null {
  try {
    const raw = sessionStorage.getItem(RUN_KEY);
    if (!raw) return null;
    const kept = JSON.parse(raw) as { version: number; at: number; ctx: TourContext; index: number };
    if (kept.version !== TOUR_VERSION || Date.now() - kept.at > RUN_TTL_MS) return null;
    const stops = stopsFor(kept.ctx);
    return stops[kept.index] ? { ctx: kept.ctx, stops, index: kept.index } : null;
  } catch {
    return null;
  }
}

export function TourProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  // A tour this tab was in the middle of carries on after a reload: embedded, some hosts
  // reload the frame on each of the app's screen changes.
  const [run, setRun] = useState<Run | null>(() => (enabled ? keptRun() : null));
  // A move that would leave unsaved edits behind, waiting for the person's say.
  const [pending, setPending] = useState<(() => void) | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const asked = useRef(run !== null);

  useEffect(() => {
    keepRun(run);
  }, [run]);

  /** Do `go`, which takes the person to `to`; first asking, when that leaves unsaved edits behind. */
  const leaving = useCallback(
    (to: string | undefined, go: () => void) => {
      if (to !== undefined && to !== location.pathname + location.search && hasUnsaved()) setPending(() => go);
      else go();
    },
    [location.pathname, location.search],
  );

  const start = useCallback(() => {
    leaving("/home", () => void resolveContext().then((ctx) => setRun({ ctx, stops: stopsFor(ctx), index: 0 })));
  }, [leaving]);

  // The first time a person opens Home. Only Home: someone who followed a link
  // to a particular draft isn't taken away from it.
  useEffect(() => {
    if (!enabled || asked.current || location.pathname !== "/home") return;
    asked.current = true;
    void alreadySeen().then((seen) => {
      if (!seen) start();
    });
  }, [enabled, location.pathname, start]);

  const end = useCallback((outcome: "finished" | "skipped") => {
    setRun(null);
    void record(outcome);
  }, []);

  /** Move to stop `index`, asking first when it is on another screen and this one has unsaved edits. */
  const moveTo = (index: number) => {
    if (!run) return;
    const to = run.stops[index]?.route?.(run.ctx);
    leaving(to, () => setRun((r) => (r ? { ...r, index } : r)));
  };

  const value = useMemo(() => ({ start, active: run !== null }), [start, run]);
  const stop = run?.stops[run.index];
  const last = run !== null && run.index + 1 >= run.stops.length;
  const action = run && stop?.action ? stop.action(run.ctx) : null;

  return (
    <TourContextValue.Provider value={value}>
      {children}
      {run && stop && (
        <TourStopView
          key={stop.id}
          stop={stop}
          index={run.index}
          total={run.stops.length}
          paused={pending !== null}
          action={action}
          onBack={() => run.index > 0 && moveTo(run.index - 1)}
          onNext={() => (last ? end("finished") : moveTo(run.index + 1))}
          onAction={() =>
            action &&
            leaving(action.to, () => {
              navigate(action.to);
              end("finished");
            })
          }
          // Leaving from the last stop, the person has seen it all.
          onSkip={() => end(last ? "finished" : "skipped")}
          to={stop.route?.(run.ctx)}
        />
      )}
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
        title="Leave without saving?"
        confirmLabel="Leave without saving"
        tone="danger"
        onConfirm={() => {
          const go = pending;
          setPending(null);
          go?.();
        }}
      >
        This draft has changes you haven't saved. The tour opens another screen, and they would be lost. To keep them, cancel and
        save first.
      </ConfirmDialog>
    </TourContextValue.Provider>
  );
}

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** The first selector, in order, with an element that is laid out (a phone-hidden copy is skipped). */
function findTarget(selectors: string[]): HTMLElement | null {
  for (const selector of selectors) {
    for (const el of Array.from(document.querySelectorAll<HTMLElement>(selector))) {
      if (el.getClientRects().length > 0) return el;
    }
  }
  return null;
}

/** Wholly outside the window. */
function offScreen(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  return r.bottom <= 0 || r.top >= window.innerHeight;
}

/** An element's box in document coordinates. */
function boxOf(el: HTMLElement): Box {
  const r = el.getBoundingClientRect();
  return { top: r.top + window.scrollY, left: r.left + window.scrollX, width: r.width, height: r.height };
}

/** Render again when the page scrolls or resizes: where the card fits depends on what is in view. */
function useScrollTick() {
  const [, setTick] = useState(0);
  useEffect(() => {
    let frame = 0;
    const bump = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        setTick((t) => t + 1);
      });
    };
    window.addEventListener("scroll", bump, { passive: true });
    window.addEventListener("resize", bump);
    return () => {
      window.removeEventListener("scroll", bump);
      window.removeEventListener("resize", bump);
      cancelAnimationFrame(frame);
    };
  }, []);
}

const sameBox = (a: Box | null, b: Box | null) =>
  a === b || (a !== null && b !== null && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);

/** Typing in a field: the arrow keys belong to it. */
const editing = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(n, hi));

/** Which side of the card faces what it points at. */
type Side = "top" | "bottom" | "left" | "right";

function TourStopView({
  stop,
  index,
  total,
  paused,
  action,
  onBack,
  onNext,
  onAction,
  onSkip,
  to,
}: {
  stop: ShownStop;
  index: number;
  total: number;
  /** A question is open over the tour: its keys are the question's. */
  paused: boolean;
  action: { label: string; to: string } | null;
  onBack: () => void;
  onNext: () => void;
  onAction: () => void;
  onSkip: () => void;
  /** Where the stop is shown, resolved when the tour started. */
  to: string | undefined;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const embedded = inIframe();
  const visible = useVisibleArea();
  useScrollTick();
  const hasTarget = stop.target.length > 0;
  const [box, setBox] = useState<Box | null>(null);
  const [gaveUp, setGaveUp] = useState(!hasTarget);
  const [entered, setEntered] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState(0);
  const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Go where the stop is, once.
  useEffect(() => {
    if (to && to !== location.pathname + location.search) navigate(to);
    // Only on arriving at the stop; the screen may tidy its own address afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stop.id]);

  // Find what the stop points at, and follow it while the screen settles or scrolls.
  useEffect(() => {
    if (!hasTarget) return;
    const began = Date.now();
    let scrolls = 0;
    let lastScroll = 0;
    let lastMeasure = 0;
    const tick = () => {
      const el = findTarget(stop.target);
      if (!el) {
        setBox(null);
        if (Date.now() - began > TARGET_WAIT_MS) setGaveUp(true);
        return;
      }
      // Scroll it into view, and again while the screen is still settling if something
      // (a list loading above it, a re-render) undid that; never after, so the person can scroll away.
      const now = Date.now();
      // Embedded, what part of the frame is in view is only reported on threshold crossings: ask afresh.
      if (embedded && now - lastMeasure > REMEASURE_MS) {
        lastMeasure = now;
        remeasureVisibleArea();
      }
      if (scrolls === 0 || (scrolls < 3 && now - began < SETTLE_MS && now - lastScroll > 600 && !embedded && offScreen(el))) {
        scrolls += 1;
        lastScroll = now;
        // A target taller than the window is shown from its top, not from its middle.
        const tall = el.offsetHeight > window.innerHeight * 0.8;
        el.scrollIntoView?.({ block: tall ? "start" : "center", behavior: reduce ? "auto" : "smooth" });
      }
      setGaveUp(false);
      const next = boxOf(el);
      setBox((prev) => (sameBox(prev, next) ? prev : next));
    };
    tick();
    const timer = window.setInterval(tick, POLL_MS);
    return () => window.clearInterval(timer);
    // The stop's targets don't change while it is shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stop.id]);

  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
  }, [stop.id]);

  useEffect(() => {
    if (paused) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSkip();
      else if (e.altKey || e.ctrlKey || e.metaKey || editing(e.target)) return;
      else if (e.key === "ArrowRight") onNext();
      else if (e.key === "ArrowLeft") onBack();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [paused, onSkip, onNext, onBack]);

  useLayoutEffect(() => {
    if (card.current) setCardHeight(card.current.offsetHeight);
  });

  const waiting = hasTarget && !box && !gaveUp;
  // Fade in once there is somewhere to be.
  useEffect(() => {
    if (waiting || entered) return;
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [waiting, entered]);

  const phone = window.innerWidth < 640;
  const width = Math.min(box ? 352 : 400, window.innerWidth - 32);
  let style: React.CSSProperties;
  let placement: "target" | "center" | "sheet";
  let side: Side | null = null;
  // Where along the facing side the arrow sits, from the card's top or left edge.
  let arrowAt = 0;

  // What the person can see, in document coordinates; embedded, less the band a host's header may cover.
  // The frame can't see where the host's sticky header is, and the host scrolls the frame's top under it
  // (bringing a target into view does), so the top of what is visible is always left to the header.
  const hostHeader = embedded && visible ? Math.min(HOST_HEADER_PX, visible.height * 0.2) : 0;
  const viewTop = embedded && visible ? visible.top + hostHeader : window.scrollY;
  const viewBottom = embedded && visible ? visible.top + visible.height : window.scrollY + window.innerHeight;
  const pageWidth = document.documentElement.clientWidth;
  const below = box ? box.top + box.height + 14 : 0;
  const above = box ? box.top - 14 - cardHeight : 0;
  // Next to the target only while the target itself is in view; one scrolled off isn't pointed at from the edge.
  const fitsBelow = box !== null && below >= viewTop && below + cardHeight <= viewBottom;
  const fitsAbove = box !== null && above >= viewTop && above + cardHeight <= viewBottom;
  // Neither fits where things are now, but the target and the card would fit in view together:
  // put the card below and bring it into view, rather than over what it describes.
  const fitsWith = box !== null && box.height + 14 + cardHeight <= viewBottom - viewTop - 32;
  const reveal = box !== null && !fitsBelow && !fitsAbove && fitsWith && !(phone && !embedded);

  if (box && (fitsBelow || fitsAbove || !(phone && !embedded))) {
    let top: number;
    let left = clamp(box.left, 16, pageWidth - width - 16);
    if (fitsBelow || fitsAbove || reveal) {
      top = fitsBelow || reveal ? below : above;
      side = fitsBelow || reveal ? "top" : "bottom";
      arrowAt = clamp(box.left + Math.min(box.width / 2, 48) - left, 20, width - 20);
    } else {
      // Taller than what fits (the preview, the editor): beside it if there's room, else over it,
      // either way inside what the person can see.
      // Beside it with a 12 px gap and 8 px from the page's edge (a wide target in a centered
      // column leaves just about a card's width); else over its right-hand end, which covers less
      // of what it says than its start does.
      if (box.left - width - 12 >= 8) {
        left = box.left - width - 12;
        side = "right";
      } else if (box.left + box.width + 12 + width <= pageWidth - 8) {
        left = box.left + box.width + 12;
        side = "left";
      } else {
        left = clamp(box.left + box.width - width - 16, 16, pageWidth - width - 16);
      }
      top = Math.max(viewTop + 16, Math.min(Math.max(box.top, viewTop + 16), viewBottom - cardHeight - 16));
      // Level with the middle of the part of the target in view.
      const shownMiddle = (Math.max(box.top, viewTop) + Math.min(box.top + box.height, viewBottom)) / 2;
      arrowAt = clamp(shownMiddle - top, 20, Math.max(20, cardHeight - 20));
    }
    style = { position: "absolute", top, left, width };
    placement = "target";
  } else if (phone && !embedded) {
    style = { position: "fixed", left: 16, right: 16, bottom: 16 };
    placement = "sheet";
  } else if (embedded && visible) {
    style = { position: "absolute", top: Math.round(visible.top + Math.min(96, visible.height * 0.15)), left: "50%", width, transform: "translateX(-50%)" };
    placement = "center";
  } else {
    style = { position: "fixed", top: "20%", left: "50%", width, transform: "translateX(-50%)" };
    placement = "center";
  }

  // Once per stop: scroll so the card placed below its target is in view too.
  const revealed = useRef(false);
  useEffect(() => {
    if (!reveal || revealed.current || !card.current) return;
    revealed.current = true;
    card.current.scrollIntoView?.({ block: "nearest", behavior: reduce ? "auto" : "smooth" });
  });

  const titleId = `tour-title-${stop.id}`;
  const bodyId = `tour-body-${stop.id}`;
  const last = index + 1 >= total;
  const shown = entered && !waiting;

  return createPortal(
    <>
      {box ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute z-40 rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-background"
          style={{ top: box.top - 4, left: box.left - 4, width: box.width + 8, height: box.height + 8, boxShadow: `0 0 0 9999px ${DIM}` }}
        />
      ) : (
        !waiting && <div aria-hidden="true" data-testid="tour-backdrop" className="pointer-events-none fixed inset-0 z-40" style={{ background: DIM }} />
      )}
      <div
        ref={card}
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        data-placement={placement}
        style={{
          ...style,
          visibility: waiting ? "hidden" : "visible",
          opacity: shown || reduce ? 1 : 0,
          translate: shown || reduce ? "0 0" : "0 6px",
          transition: reduce ? undefined : "opacity 150ms ease-out, translate 150ms ease-out",
        }}
        className="z-50 rounded-lg border border-border bg-popover p-4 text-popover-foreground shadow-xl"
      >
        {side && (
          <span
            aria-hidden="true"
            data-side={side}
            className={cn(
              "absolute h-3 w-3 rotate-45 border-border bg-popover",
              side === "top" && "-top-[7px] border-l border-t",
              side === "bottom" && "-bottom-[7px] border-b border-r",
              side === "left" && "-left-[7px] border-b border-l",
              side === "right" && "-right-[7px] border-r border-t",
            )}
            style={side === "top" || side === "bottom" ? { left: arrowAt - 6 } : { top: arrowAt - 6 }}
          />
        )}
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {index + 1} of {total}
          </p>
          <span aria-hidden="true" className="flex gap-1">
            {Array.from({ length: total }, (_, i) => (
              <span key={i} className={cn("h-1.5 w-1.5 rounded-full", i === index ? "bg-primary" : i < index ? "bg-primary/40" : "bg-muted-foreground/25")} />
            ))}
          </span>
        </div>
        <h2 id={titleId} ref={heading} tabIndex={-1} className="mt-1 text-base font-semibold focus:outline-none">
          {stop.title}
        </h2>
        <p id={bodyId} className="mt-2 text-sm text-muted-foreground">
          {stop.body}
        </p>
        {stop.learnMore && <LearnMore article={stop.learnMore} className="mt-2 text-sm" />}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <Button variant="ghost" onClick={onSkip}>
            {last ? "Close" : "Skip tour"}
          </Button>
          <div className="flex gap-2">
            {index > 0 && (
              <Button variant="secondary" onClick={onBack}>
                Back
              </Button>
            )}
            {action ? (
              <Button onClick={onAction}>{action.label}</Button>
            ) : (
              <Button onClick={onNext}>{last ? "Finish" : index === 0 ? "Show me around" : "Next"}</Button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
